import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, ArrowLeft, MapPin, User as UserIcon } from 'lucide-react';
import { memberPortal, useAsync } from '@fitboxx/shared';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { formatDay, formatTime } from '@/lib/utils';

interface Booking {
  id: string; status: string;
  occurrence_template_name?: string;
  occurrence_starts_at?: string;
  occurrence_location_name?: string;
  occurrence_instructor_name?: string;
  checked_in_at?: string | null;
}

const STATUS_VARIANT: Record<string, 'success' | 'warning' | 'destructive' | 'outline' | 'secondary'> = {
  confirmed: 'secondary', attended: 'success', cancelled: 'outline',
  no_show: 'destructive', late_cancel: 'warning',
};

export default function MyBookings() {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Button asChild variant="ghost" size="sm" className="-ml-2">
          <Link to="/"><ArrowLeft className="h-4 w-4 mr-1" />Schedule</Link>
        </Button>
        <h1 className="text-xl font-semibold tracking-tight">My bookings</h1>
      </div>

      <Tabs defaultValue="upcoming">
        <TabsList>
          <TabsTrigger value="upcoming">Upcoming</TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
        </TabsList>
        <TabsContent value="upcoming" className="mt-3"><BookingList kind="upcoming" /></TabsContent>
        <TabsContent value="history" className="mt-3"><BookingList kind="history" /></TabsContent>
      </Tabs>
    </div>
  );
}

function BookingList({ kind }: { kind: 'upcoming' | 'history' }) {
  const { data, loading, refresh } = useAsync(
    () => (kind === 'upcoming' ? memberPortal.bookings() : memberPortal.history()) as Promise<Booking[]>,
    [kind],
  );
  const rows = Array.isArray(data) ? data : [];
  const [busyId, setBusyId] = useState<string | null>(null);

  async function cancel(b: Booking) {
    if (!window.confirm(`Cancel ${b.occurrence_template_name || 'this booking'}?`)) return;
    setBusyId(b.id);
    try { await memberPortal.cancel(b.id); refresh(); }
    catch (e) { window.alert(`Cancel failed: ${(e as Error).message}`); }
    finally { setBusyId(null); }
  }

  if (loading) return <Skeleton className="h-24 w-full" />;
  if (rows.length === 0) {
    return (
      <Card className="p-8 text-center text-sm text-muted-foreground">
        <Calendar className="h-8 w-8 mx-auto mb-2 text-muted-foreground/60" />
        {kind === 'upcoming' ? 'No upcoming bookings.' : 'No past bookings yet.'}
      </Card>
    );
  }
  return (
    <div className="space-y-2">
      {rows.map((b) => (
        <Card key={b.id} className="p-3">
          <div className="flex items-start gap-3">
            <div className="flex-1 min-w-0">
              <div className="font-medium truncate">{b.occurrence_template_name || 'Class'}</div>
              <div className="text-xs text-muted-foreground mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
                {b.occurrence_starts_at && (
                  <span>{formatDay(b.occurrence_starts_at)} · {formatTime(b.occurrence_starts_at)}</span>
                )}
                {b.occurrence_instructor_name && <span className="inline-flex items-center gap-1"><UserIcon className="h-3 w-3" />{b.occurrence_instructor_name}</span>}
                {b.occurrence_location_name && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{b.occurrence_location_name}</span>}
              </div>
            </div>
            <div className="flex flex-col items-end gap-1.5">
              <Badge variant={STATUS_VARIANT[b.status] || 'outline'} className="capitalize">
                {b.status.replace('_', ' ')}
              </Badge>
              {kind === 'upcoming' && b.status === 'confirmed' && (
                <Button variant="ghost" size="sm" className="text-destructive h-7 px-2"
                        onClick={() => void cancel(b)} disabled={busyId === b.id}>
                  {busyId === b.id ? '…' : 'Cancel'}
                </Button>
              )}
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}
